/**
 * JSON sanitization utilities for safe cloning and preview generation
 * with memory and size bounds to prevent performance issues.
 */

export interface SanitizeOptions {
  maxChars?: number;
  maxDepth?: number;
  maxStringLength?: number;
  maxArrayItems?: number;
  maxObjectKeys?: number;
}

export interface SanitizeResult {
  clone: unknown;
  size: number;
}

const DEFAULT_OPTIONS: Required<SanitizeOptions> = {
  maxChars: 10_000,
  maxDepth: 6,
  maxStringLength: 500,
  maxArrayItems: 100,
  maxObjectKeys: 100,
};

export function sanitizeClone(value: unknown, options: SanitizeOptions = {}): SanitizeResult {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const seen = new WeakSet();
  let charCount = 0;
  let truncated = false;

  function sanitizeValue(val: unknown, depth: number): unknown {
    if (truncated || charCount > opts.maxChars) {
      truncated = true;
      return '[…truncated…]';
    }
    if (depth > opts.maxDepth) {
      return '[…max depth reached…]';
    }
    if (val === null || val === undefined) {
      charCount += 4;
      return val;
    }
    const type = typeof val;
    if (type === 'boolean' || type === 'number') {
      charCount += String(val).length;
      return val;
    }
    if (type === 'string') {
      const str = val as string;
      const limited = str.length > opts.maxStringLength 
        ? str.slice(0, opts.maxStringLength) + '…'
        : str;
      charCount += limited.length + 2;
      return limited;
    }
    if (type === 'function') {
      const name = (val as Function).name || 'anonymous';
      const result = `[Function: ${name}]`;
      charCount += result.length;
      return result;
    }
    if (type === 'symbol') {
      const result = `[Symbol: ${String(val)}]`;
      charCount += result.length;
      return result;
    }
    if (type === 'bigint') {
      const result = `${String(val)}n`;
      charCount += result.length;
      return result;
    }
    if (type === 'object') {
      if (seen.has(val as object)) {
        charCount += 11;
        return '[Circular]';
      }
      seen.add(val as object);
      try {
        if (val instanceof Date) {
          const result = val.toISOString();
          charCount += result.length + 2;
          return result;
        }
        if (val instanceof RegExp) {
          const result = String(val);
          charCount += result.length;
          return result;
        }
        if (val instanceof Error) {
          const result = {
            name: val.name,
            message: val.message,
            stack: val.stack?.slice(0, 200) + (val.stack && val.stack.length > 200 ? '…' : ''),
          };
          return sanitizeValue(result, depth);
        }
        if (ArrayBuffer.isView(val)) {
          const view = val as ArrayBufferView;
          const result = {
            type: val.constructor.name,
            byteLength: view.byteLength,
            length: (val as any).length,
          };
          return sanitizeValue(result, depth);
        }
        if (val instanceof ArrayBuffer) {
          const result = {
            type: 'ArrayBuffer',
            byteLength: val.byteLength,
          };
          return sanitizeValue(result, depth);
        }
        if (Array.isArray(val)) {
          const arr: unknown[] = [];
          const maxItems = Math.min(val.length, opts.maxArrayItems);
          for (let i = 0; i < maxItems; i++) {
            if (truncated) break;
            arr.push(sanitizeValue(val[i], depth + 1));
          }
          if (val.length > opts.maxArrayItems) {
            arr.push(`[…${val.length - opts.maxArrayItems} more items…]`);
          }
          return arr;
        }
        const obj: Record<string, unknown> = {};
        const keys = Object.keys(val as object);
        const maxKeys = Math.min(keys.length, opts.maxObjectKeys);
        for (let i = 0; i < maxKeys; i++) {
          if (truncated) break;
          const key = keys[i];
          obj[key] = sanitizeValue((val as any)[key], depth + 1);
        }
        if (keys.length > opts.maxObjectKeys) {
          obj['[…more keys…]'] = `${keys.length - opts.maxObjectKeys} additional keys`;
        }
        return obj;
      } finally {
        seen.delete(val as object);
      }
    }
    const result = `[${type}]`;
    charCount += result.length;
    return result;
  }

  try {
    const clone = sanitizeValue(value, 0);
    const jsonString = JSON.stringify(clone);
    const size = jsonString.length;
    if (truncated || size > opts.maxChars) {
      return {
        clone: {
          __truncated__: true,
          preview: makePreview(value),
          approxSize: charCount,
          originalType: typeof value,
        },
        size: Math.min(size, opts.maxChars),
      };
    }
    return { clone, size };
  } catch (error) {
    return {
      clone: {
        __unserializable__: true,
        preview: makePreview(value),
        error: error instanceof Error ? error.message : 'Unknown serialization error',
        originalType: typeof value,
      },
      size: 0,
    };
  }
}

export function makePreview(value: unknown): string {
  try {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    const type = typeof value;
    if (type === 'boolean' || type === 'number' || type === 'bigint') {
      return String(value);
    }
    if (type === 'string') {
      const str = value as string;
      if (str.length <= 80) return `"${str}"`;
      return `"${str.slice(0, 77)}…"`;
    }
    if (type === 'function') {
      const name = (value as Function).name || 'anonymous';
      return `[Function: ${name}]`;
    }
    if (type === 'symbol') {
      return `[Symbol]`;
    }
    if (type === 'object') {
      if (value instanceof Date) {
        return value.toISOString();
      }
      if (value instanceof RegExp) {
        return String(value);
      }
      if (value instanceof Error) {
        return `[Error: ${value.message}]`;
      }
      if (ArrayBuffer.isView(value)) {
        const view = value as ArrayBufferView;
        return `[${value.constructor.name}: ${(value as any).length || view.byteLength} bytes]`;
      }
      if (value instanceof ArrayBuffer) {
        return `[ArrayBuffer: ${value.byteLength} bytes]`;
      }
      if (Array.isArray(value)) {
        return `[${value.length}]`;
      }
      const keys = Object.keys(value);
      return `{${keys.length} keys}`;
    }
    return `[${type}]`;
  } catch {
    return '…';
  }
}

export function makeOutputPreview(outputs: unknown, pinId?: string): string {
  if (!outputs || typeof outputs !== 'object') {
    return makePreview(outputs);
  }
  if (pinId && pinId in (outputs as any)) {
    return makePreview((outputs as any)[pinId]);
  }
  return makePreview(outputs);
}
